import { useCallback, useEffect, useState } from 'react';

// The build gives main.js a new content hash on every release, so the file name index.html
// points at is the version - nothing has to be stamped or maintained by hand.
const MAIN_JS = /\/static\/js\/main\.\w+\.js/;

const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Long enough that the reload does not come as a surprise, short enough that nobody starts
// something new in the meantime.
const RELOAD_COUNTDOWN_SECONDS = 30;

// Keeps a reload that does not resolve the difference from turning into a reload loop, and
// keeps a later check from restarting a countdown that is already running.
const RELOAD_ATTEMPT_KEY = 'work.reloadedForVersion';

/** The main.js the page running in this browser was booted from. */
export const getLoadedVersion = () =>
  document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]')?.getAttribute('src') ?? undefined;

/** The main.js the server hands out right now, or undefined if we cannot tell. */
export const fetchDeployedVersion = async () => {
  // The timestamp is belt and braces on top of no-store: iOS is inconsistent about honouring
  // the fetch cache mode, and a cached index.html would report the old version forever.
  const response = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' });

  return response.ok ? MAIN_JS.exec(await response.text())?.[0] : undefined;
};

// Starting the countdown under someone who is typing would throw away what they have not
// saved yet. The next check picks the new version up once they are done.
const isTyping = () => {
  const element = document.activeElement;

  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    (element instanceof HTMLElement && element.isContentEditable)
  );
};

/**
 * Reloads WORK once a new version has been released, after counting down so the user is not
 * interrupted without warning. Without this the app keeps running the JavaScript it loaded on
 * its first visit, and on an iPad - where WORK is a home screen app that is never really
 * closed - that can be weeks of old code.
 */
const useAppUpdate = () => {
  const [secondsUntilReload, setSecondsUntilReload] = useState<number>();

  const check = useCallback(async () => {
    const deployedVersion = await fetchDeployedVersion().catch(() => undefined);

    // A failed request means we are offline, not that a new version is out.
    if (!deployedVersion || deployedVersion === getLoadedVersion()) return;
    if (isTyping() || sessionStorage.getItem(RELOAD_ATTEMPT_KEY) === deployedVersion) return;

    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, deployedVersion);
    setSecondsUntilReload(RELOAD_COUNTDOWN_SECONDS);
  }, []);

  useEffect(() => {
    // Only production builds hash their file names, so only they have anything to compare.
    if (process.env.NODE_ENV !== 'production') return;

    // Checking when the app becomes visible again is what catches up an iPad that has had the
    // app open in the background since before the release.
    const checkWhenVisible = () => !document.hidden && check();

    check();
    const interval = setInterval(checkWhenVisible, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkWhenVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, [check]);

  useEffect(() => {
    if (secondsUntilReload === undefined) return;

    if (secondsUntilReload <= 0) {
      window.location.reload();
      return;
    }

    const tick = setTimeout(() => setSecondsUntilReload(secondsUntilReload - 1), 1000);

    return () => clearTimeout(tick);
  }, [secondsUntilReload]);

  return { secondsUntilReload, reloadNow: () => window.location.reload() };
};

export default useAppUpdate;
